import { useCallback, useEffect, useRef, useState } from 'react';
import { ncfAPI } from '../../services/api';
import { buildNcfReviewRoot, getPath, linkedCorporationPatch, setPath } from '../../utils/ncfReviewRoot.js';
import { isFieldEffectivelyRequired, evaluateRequirementRules } from '../../utils/ncfRequirementRules.js';
import AddressInput from './AddressInput.jsx';
import NcfPlacementWorksheet, { WORKSHEET_PATHS } from './NcfPlacementWorksheet.jsx';
import { applyPlacementCalculation, synchronizePlacementInputs, financialSignature, isPermanentPlacement, FEE_PERCENT, FEE_PERCENT_UNIT } from '../../../../backend/src/services/ncfFinancials.mjs';
import { getFieldValidationError } from '../../../../backend/src/services/ncfFieldValidation.mjs';
import { PROJECT_CHANGE_REASON, JOB_DATE_PATHS, CHANGE_SYSTEM_PATHS, isMissingChangeValue } from '../../../../backend/src/services/ncfJobChange.mjs';
import Typeahead from '../Typeahead.jsx';
import NcfSameAsManager from './NcfSameAsManager.jsx';

const getProblemFields = (err) => {
  const data = err?.response?.data || {};
  if (data.invalidFields?.length) {
    return data.invalidFields.map(field => ({ ...field, fieldGroup: field.message }));
  }
  if (data.incompleteAddresses?.length) {
    return data.incompleteAddresses.map(item => ({ sourcePath: item.sourcePath, label: item.label, fieldGroup: `missing ${item.missing.join(', ')}` }));
  }
  return data.missingFields || [];
};

const prettifyPath = (path) => String(path).split('.').pop().replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^\w/, char => char.toUpperCase());
const toInputValue = (value, format) => {
  const text = value == null ? '' : String(value);
  if (format !== 'date' || !text) return text;
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
  const date = new Date(text);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
};
const isFilledValue = (value) => {
  const text = String(value ?? '').trim();
  return text !== '' && !/^select(?:\.\.\.|…)$/i.test(text) && !/^mm\/dd\/yyyy$/i.test(text);
};
const MAX_CHANGE_NOTE_LENGTH = 1000;
const SECTION_FILTER_THRESHOLD = 8;
const groupChanges = (changes) => changes.reduce((groups, change) => {
  const name = change.fieldGroup || 'Other';
  const group = groups.find(item => item.name === name);
  if (group) group.changes.push(change);
  else groups.push({ name, changes: [change] });
  return groups;
}, []);

function NcfChangeModal({ ncfId, ncf: providedNcf = null, fieldInventory: providedFields = null, sectionVisibility: providedVisibility = null, onClose, onSubmitted }) {
  const [reasons, setReasons] = useState([]);
  const [sections, setSections] = useState([]);
  const [visibilityRules, setVisibilityRules] = useState(providedVisibility || []);
  const [inventoryFields, setInventoryFields] = useState(providedFields || []);
  const [root, setRoot] = useState(null);
  const [selected, setSelected] = useState([]);
  const [selectedSections, setSelectedSections] = useState([]);
  const [sectionFilter, setSectionFilter] = useState('');
  const [changeNote, setChangeNote] = useState('');
  const [values, setValues] = useState({});
  const [preview, setPreview] = useState(null);
  const [missingFields, setMissingFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [reviewedSignature, setReviewedSignature] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobChange, setJobChange] = useState(null);
  const jobRequestRef = useRef(0);
  const fields = [
    ...inventoryFields,
    ...(jobChange?.requiredFields || []).filter(field => !inventoryFields.some(item => item.sourcePath === field.sourcePath))
  ];

  useEffect(() => () => { jobRequestRef.current += 1; }, []);

  const fetchJobs = useCallback(async (query, { signal } = {}) => {
    const { data } = await ncfAPI.searchJobs(query, 15, { signal });
    return (data.jobs || []).map(job => ({
      id: String(job.id),
      label: job.reference || String(job.id),
      sublabel: [job.title, job.company, `Job ID ${job.id}`].filter(Boolean).join(' - ')
    }));
  }, []);

  useEffect(() => {
    let active = true;
    Promise.all([
      providedNcf ? Promise.resolve({ data: { ncf: providedNcf } }) : ncfAPI.get(ncfId),
      providedFields ? Promise.resolve({ data: { fields: providedFields, sectionVisibility: providedVisibility } }) : ncfAPI.fields()
    ]).then(async ([ncfResponse, fieldsResponse]) => {
      const ncf = ncfResponse.data.ncf;
      const loadedRoot = buildNcfReviewRoot(ncf, ncf.reviewedData || {});
      synchronizePlacementInputs(loadedRoot, fieldsResponse.data.fields || []);
      try { applyPlacementCalculation(loadedRoot, fieldsResponse.data.fields || []); } catch (_error) {}
      const [reasonsResponse, visibilityResponse] = await Promise.all([
        ncfAPI.changeReasons(isPermanentPlacement(loadedRoot)),
        fieldsResponse.data.sectionVisibility ? Promise.resolve(fieldsResponse) : ncfAPI.fields().catch(() => ({ data: {} }))
      ]);
      if (!active) return;
      setReasons(reasonsResponse.data.reasons || []);
      setSections(reasonsResponse.data.sections || []);
      setVisibilityRules(visibilityResponse.data.sectionVisibility || []);
      setInventoryFields(fieldsResponse.data.fields || []);
      setRoot(loadedRoot);
    }).catch(err => {
      if (active) setError(err?.response?.data?.error || 'Could not load this NCF for changes.');
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [ncfId, providedNcf, providedFields, providedVisibility]);

  // Requirement rules are evaluated against the edited values, matching the main form's live behavior.
  const workingRoot = (() => {
    if (!root) return null;
    const next = JSON.parse(JSON.stringify(root));
    if (jobChange && selected.includes(PROJECT_CHANGE_REASON)) {
      next.jobdiva ||= {};
      next.jobdiva.job = structuredClone(jobChange.job);
      next.jobdiva.submittal = structuredClone(jobChange.submittal || {});
      Object.entries(jobChange.fieldValues).forEach(([path, value]) => setPath(next, path, value));
    }
    const activePaths = new Set(reasons.filter(reason => selected.includes(reason.label)).flatMap(reason => reason.fields.map(field => typeof field === 'string' ? field : field.path)));
    sections.filter(section => selectedSections.includes(section.name)).forEach(section => section.fields.forEach(path => activePaths.add(path)));
    Object.keys(jobChange?.fieldValues || {}).forEach(path => activePaths.add(path));
    Object.entries(values).filter(([path]) => activePaths.has(path)).forEach(([path, value]) => setPath(next, path, value));
    const requirementRoot = structuredClone(next);
    Object.entries(values).forEach(([path, value]) => setPath(requirementRoot, path, value));
    fields.filter(field => !CHANGE_SYSTEM_PATHS.has(field.sourcePath))
      .filter(field => (isFieldEffectivelyRequired(field, requirementRoot)
        && (isMissingChangeValue(field, getPath(next, field.sourcePath))
          || isMissingChangeValue(field, getPath(root, field.sourcePath))))
        || getFieldValidationError(field.format, getPath(root, field.sourcePath))
        || missingFields.some(item => item.sourcePath === field.sourcePath))
      .forEach(field => activePaths.add(field.sourcePath));
    Object.entries(values).filter(([path]) => activePaths.has(path)).forEach(([path, value]) => setPath(next, path, value));
    try { applyPlacementCalculation(next, fields); } catch (_error) {}
    return next;
  })();
  const financialChanged = Boolean(root && financialSignature(workingRoot, fields) !== financialSignature(root, fields));
  const currentSignature = workingRoot ? financialSignature(workingRoot, fields) : '';
  const financialReviewed = reviewedSignature === currentSignature;
  const showWorksheet = isPermanentPlacement(workingRoot) && (financialChanged || selected.some(label => /Rate Change$/.test(label)));

  const describeField = (field) => {
    const path = typeof field === 'string' ? field : field.path;
    const inventoryField = fields.find(item => item.sourcePath === path);
    return {
      path,
      label: inventoryField?.label || prettifyPath(path),
      format: inventoryField?.format || 'text',
      options: inventoryField?.options || null,
      isMultiline: Boolean(inventoryField?.isMultiline),
      required: Boolean(jobChange?.requiredFields.some(item => item.sourcePath === path)
        || (inventoryField && workingRoot && isFieldEffectivelyRequired(inventoryField, workingRoot))),
      ...(typeof field === 'object' ? field : {}),
      ...(JOB_DATE_PATHS.includes(path) ? { format: 'date', isMultiline: false, options: null } : {})
    };
  };
  const originalValue = (field) => toInputValue(getPath(root, field.path), field.format);
  const currentValue = (field) => toInputValue(getPath(workingRoot, field.path), field.format);
  const reasonFields = reasons
    .filter(reason => selected.includes(reason.label))
    .flatMap(reason => reason.fields.map(describeField));
  const isSectionVisible = (name) => {
    const rule = visibilityRules.find(item => item.sectionName === name);
    if (!rule || rule.visibilityMode === 'always' || !workingRoot) return true;
    const matches = evaluateRequirementRules(rule.visibilityRules, workingRoot);
    return rule.visibilityMode === 'hideWhen' ? !matches : matches;
  };
  const availableSections = sections.filter(section => selectedSections.includes(section.name) || isSectionVisible(section.name));
  const filteredSections = availableSections.filter(section => !sectionFilter.trim()
    || section.name.toLowerCase().includes(sectionFilter.trim().toLowerCase()));
  const activeSectionGroups = sections
    .filter(section => selectedSections.includes(section.name))
    .map(section => ({
      name: section.name,
      visible: isSectionVisible(section.name),
      fields: section.fields
        .filter(path => !reasonFields.some(field => field.path === path))
        .map(describeField)
    }));
  const sectionFields = activeSectionGroups.filter(section => section.visible).flatMap(section => section.fields);
  const hasSelection = selected.length > 0 || selectedSections.length > 0;
  const additionalFields = !hasSelection ? [] : fields
    .filter(field => !CHANGE_SYSTEM_PATHS.has(field.sourcePath))
    .filter(field => !reasonFields.some(item => item.path === field.sourcePath))
    .filter(field => !sectionFields.some(item => item.path === field.sourcePath))
    .filter(field => {
      const described = describeField(field.sourcePath);
      const changedByJob = jobChange && Object.prototype.hasOwnProperty.call(jobChange.fieldValues, field.sourcePath)
        && toInputValue(jobChange.fieldValues[field.sourcePath], described.format) !== originalValue(described);
      const missingRequired = described.required
        && (isMissingChangeValue(field, getPath(root, field.sourcePath))
          || isMissingChangeValue(field, getPath(workingRoot, field.sourcePath))
          || Object.prototype.hasOwnProperty.call(values, field.sourcePath));
      return changedByJob || missingRequired
        || getFieldValidationError(field.format, getPath(root, field.sourcePath))
        || missingFields.some(item => item.sourcePath === field.sourcePath);
    })
    .map(field => describeField(field.sourcePath));
  const selectedFields = [...reasonFields, ...sectionFields, ...additionalFields];

  const selectJob = async (option) => {
    const requestId = ++jobRequestRef.current;
    setPreview(null);
    setReviewedSignature(null);
    setError('');
    setMissingFields([]);
    const resetPaths = new Set([
      ...Object.keys(jobChange?.fieldValues || {}),
      ...(reasons.find(reason => reason.label === PROJECT_CHANGE_REASON)?.fields || []).map(field => typeof field === 'string' ? field : field.path)
    ]);
    if (!option) {
      setValues(previous => Object.fromEntries(Object.entries(previous).filter(([path]) => !resetPaths.has(path))));
      setSelectedJob(null);
      setJobChange(null);
      return;
    }
    setBusy(true);
    try {
      const { data } = await ncfAPI.getJobChange(ncfId, option.id);
      if (requestId !== jobRequestRef.current) return;
      Object.keys(data.fieldValues).forEach(path => resetPaths.add(path));
      setValues(previous => Object.fromEntries(Object.entries(previous).filter(([path]) => !resetPaths.has(path))));
      setRoot(data.root);
      setSelectedJob(option);
      setJobChange(data);
    } catch (err) {
      if (requestId === jobRequestRef.current) setError(err?.response?.data?.error || 'Could not load the selected job.');
    } finally {
      if (requestId === jobRequestRef.current) setBusy(false);
    }
  };

  const toggleReason = (label) => {
    setPreview(null);
    setReviewedSignature(null);
    setMissingFields([]);
    if (label === PROJECT_CHANGE_REASON && selected.includes(label)) selectJob(null);
    if (selected.includes(label)) {
      const remainingPaths = new Set(reasons.filter(reason => reason.label !== label && selected.includes(reason.label))
        .flatMap(reason => reason.fields.map(field => typeof field === 'string' ? field : field.path)));
      const removedPaths = new Set((reasons.find(reason => reason.label === label)?.fields || [])
        .map(field => typeof field === 'string' ? field : field.path)
        .filter(path => !remainingPaths.has(path)));
      setValues(previous => Object.fromEntries(Object.entries(previous).filter(([path]) => !removedPaths.has(path))));
    }
    setSelected(previous => (previous.includes(label) ? previous.filter(item => item !== label) : [...previous, label]));
  };

  const toggleSection = (name) => {
    setPreview(null);
    setReviewedSignature(null);
    setMissingFields([]);
    if (selectedSections.includes(name)) {
      const removedPaths = new Set(sections.find(section => section.name === name)?.fields || []);
      setValues(previous => Object.fromEntries(Object.entries(previous).filter(([path]) => !removedPaths.has(path))));
    }
    setSelectedSections(previous => (previous.includes(name) ? previous.filter(item => item !== name) : [...previous, name]));
  };

  const setValue = (path, value) => {
    setPreview(null);
    setReviewedSignature(null);
    setValues(previous => {
      const patch = { [path]: value };
      return { ...previous, ...patch, ...linkedCorporationPatch(workingRoot, patch) };
    });
  };

  const changedFieldValues = () => {
    const changed = Object.fromEntries(selectedFields
      .filter((field, index, list) => list.findIndex(item => item.path === field.path) === index)
      .filter(field => Object.prototype.hasOwnProperty.call(values, field.path))
      .filter(field => !jobChange || values[field.path] !== jobChange.fieldValues[field.path])
      .filter(field => jobChange || currentValue(field) !== originalValue(field))
      .map(field => [field.path, currentValue(field)]));
    if (Object.prototype.hasOwnProperty.call(changed, FEE_PERCENT) && isPermanentPlacement(workingRoot)) changed[FEE_PERCENT_UNIT] = 'percent';
    return changed;
  };

  const changePayload = (fieldValues) => ({
    changeReasons: selected,
    changeSections: selectedSections,
    changeNote: changeNote.trim() || undefined,
    fieldValues,
    jobId: selectedJob?.id,
    jobSnapshot: jobChange ? JSON.stringify(jobChange.fieldValues) : undefined
  });

  const review = async () => {
    setError('');
    setMissingFields([]);
    if (!hasSelection) {
      setError('Select at least one change reason or form section.');
      return;
    }
    const fieldValues = changedFieldValues();
    if (Object.keys(fieldValues).length === 0 && !selectedJob) {
      setError('Change at least one field before reviewing.');
      return;
    }
    const invalidFields = selectedFields.filter(field => getFieldValidationError(field.format, currentValue(field)));
    if (invalidFields.length > 0) {
      setMissingFields(invalidFields.map(field => ({
        sourcePath: field.path,
        label: field.label,
        fieldGroup: getFieldValidationError(field.format, currentValue(field))
      })));
      setError('Correct invalid email addresses and phone numbers before reviewing.');
      return;
    }
    const emptyRequired = selectedFields
      .filter((field, index, list) => list.findIndex(item => item.path === field.path) === index)
      .filter(field => field.required && isMissingChangeValue({ ...field, sourcePath: field.path }, currentValue(field)));
    if (emptyRequired.length > 0) {
      setMissingFields(emptyRequired.map(field => ({ sourcePath: field.path, label: field.label, fieldGroup: 'required' })));
      setError('Fill in the required fields before reviewing.');
      return;
    }
    setBusy(true);
    try {
      const response = await ncfAPI.previewChange(ncfId, changePayload(fieldValues));
      if ((response.data.changes || []).length === 0) setError('No changes were found to submit to HR.');
      else setPreview({ reason: response.data.reason, changes: response.data.changes, fieldValues, financialReviewRequired: response.data.financialReviewRequired });
    } catch (err) {
      setMissingFields(getProblemFields(err));
      setError(err?.response?.data?.error || 'Could not review this change.');
    } finally { setBusy(false); }
  };

  const submit = async () => {
    if (!preview) return;
    setBusy(true);
    setError('');
    try {
      const response = await ncfAPI.submitChange(ncfId, {
        ...changePayload(preview.fieldValues),
        financialReview: financialReviewed ? { signature: reviewedSignature } : undefined
      });
      await onSubmitted?.(response.data);
    } catch (err) {
      setMissingFields(getProblemFields(err));
      setError(err?.response?.data?.error || 'Could not submit this change to HR.');
      setBusy(false);
    }
  };

  const renderInput = (field) => {
    const inputId = `ncf-change-${field.path.replace(/[^a-z0-9]+/gi, '-')}`;
    const value = currentValue(field);
    const validationError = getFieldValidationError(field.format, value);
    const validationProps = {
      'aria-invalid': Boolean(validationError),
      'aria-describedby': validationError ? `${inputId}-error` : undefined
    };
    const options = field.options?.length > 0
      ? field.options
      : field.format === 'boolean' ? ['Yes', 'No'] : null;
    let control;
    if (field.path === 'assignment.jobReference' && selected.includes(PROJECT_CHANGE_REASON)) {
      control = <><Typeahead id={inputId} value={selectedJob} onSelect={selectJob} fetchOptions={fetchJobs} minChars={2} disabled={busy} placeholder="Enter a JobDiva job ID or reference number..." /><small>Current reference: {originalValue(field) || 'Not set'}. Select a job to load its details.</small></>;
    } else if (field.format === 'address') {
      control = <AddressInput id={inputId} value={value} onChange={nextValue => setValue(field.path, nextValue)} disabled={busy} required={Boolean(field.required)} multiline />;
    } else if (options) {
      const choices = value && !options.includes(value) ? [value, ...options] : options;
      control = <select id={inputId} value={value} {...validationProps} onChange={event => setValue(field.path, event.target.value)} disabled={busy}><option value="">Select...</option>{choices.map(option => <option key={option} value={option}>{option}</option>)}</select>;
    } else if (field.isMultiline) {
      control = <textarea id={inputId} value={value} {...validationProps} onChange={event => setValue(field.path, event.target.value)} rows="3" disabled={busy} />;
    } else {
      control = <input id={inputId} type={['date', 'email', 'tel'].includes(field.format) ? field.format : 'text'} value={value} {...validationProps} onChange={event => setValue(field.path, event.target.value)} disabled={busy} />;
    }
    const changed = value !== originalValue(field);
    const isComplete = !isMissingChangeValue({ ...field, sourcePath: field.path }, value) && !validationError;
    const requiredMark = field.required && <span className={`ncf-required-mark ${isComplete ? 'complete' : 'incomplete'}`}>{isComplete ? 'Complete' : 'Required'}</span>;
    const input = <label key={field.path} htmlFor={inputId} className={changed ? 'ncf-update-field-highlight' : undefined}>{field.label}{requiredMark}{control}{validationError && <small id={`${inputId}-error`} role="alert">{validationError}</small>}</label>;
    return field.path === 'jobdiva.job.timesheetApproverName'
      && selectedFields.some(item => item.path === 'jobdiva.job.timesheetApproverEmail')
      ? <div key={field.path} className="ncf-approver-field">{input}<NcfSameAsManager root={workingRoot} disabled={busy} onCopy={patch => {
        setPreview(null);
        setValues(previous => ({ ...previous, ...patch }));
      }} /></div>
      : input;
  };

  return <div className="modal-overlay" onClick={() => !busy && onClose()}>
    <div className="modal-content ncf-update-modal ncf-change-modal" onClick={event => event.stopPropagation()}>
      <div className="modal-header"><h2>Change NCF</h2><button type="button" onClick={onClose} className="close-btn" disabled={busy}>✕</button></div>
      {loading ? <p>Loading...</p> : <>
        <p>Select what is changing. The changes are submitted to HR and added to this NCF&apos;s history.</p>
        {error && <div className="alert alert-error">{error}</div>}
        {missingFields.length > 0 && <ul className="ncf-missing-fields-list">{missingFields.map(field => <li key={field.sourcePath}><strong>{field.label}</strong> <small>{field.fieldGroup}</small></li>)}</ul>}
        <fieldset className="ncf-change-reasons" disabled={busy}>
          <legend>Common changes</legend>
          {reasons.map(reason => <label key={reason.label} className="ncf-inline-check"><input type="checkbox" checked={selected.includes(reason.label)} onChange={() => toggleReason(reason.label)} /> {reason.label}</label>)}
        </fieldset>
        {sections.length > 0 && <fieldset className="ncf-change-reasons ncf-change-sections" disabled={busy}>
          <legend>Other changes</legend>
          <p className="ncf-change-hint">Select a form section to edit its fields, such as a new timesheet approver.</p>
          {availableSections.length > SECTION_FILTER_THRESHOLD && <input type="search" className="ncf-change-section-filter" aria-label="Filter form sections" placeholder="Filter sections..." value={sectionFilter} onChange={event => setSectionFilter(event.target.value)} />}
          {filteredSections.map(section => <label key={section.name} className="ncf-inline-check"><input type="checkbox" checked={selectedSections.includes(section.name)} onChange={() => toggleSection(section.name)} /> {section.name}</label>)}
          {filteredSections.length === 0 && <small>No sections match the filter.</small>}
        </fieldset>}
        {reasons.filter(reason => selected.includes(reason.label)).map(reason => <fieldset key={reason.label} className="ncf-change-fields">
          <legend>{reason.label}</legend>
          <div className="ncf-review-grid">{reason.fields.map(describeField).filter(field => !field.hidden && !(showWorksheet && WORKSHEET_PATHS.includes(field.path))).map(renderInput)}</div>
        </fieldset>)}
        {activeSectionGroups.map(section => <fieldset key={`section-${section.name}`} className="ncf-change-fields">
          <legend>{section.name}</legend>
          {section.visible
            ? <div className="ncf-review-grid">{section.fields.filter(field => !(showWorksheet && WORKSHEET_PATHS.includes(field.path))).map(renderInput)}</div>
            : <p className="ncf-change-hint">This section no longer applies to this NCF because of other changes. Its fields will not be changed.</p>}
        </fieldset>)}
        {additionalFields.length > 0 && <fieldset className="ncf-change-fields">
          <legend>Additional changes and required information</legend>
          <p>Review fields affected by the selected job and complete missing required information. Blank values from the new job replace the old values.</p>
          <div className="ncf-review-grid">{additionalFields.filter(field => !(showWorksheet && WORKSHEET_PATHS.includes(field.path))).map(renderInput)}</div>
        </fieldset>}
        {showWorksheet && <NcfPlacementWorksheet root={workingRoot} fields={fields} disabled={busy} onChange={patch => {
          setPreview(null);
          setReviewedSignature(null);
          setValues(previous => ({ ...previous, ...patch }));
        }} />}
        {(financialChanged || preview?.financialReviewRequired) && <label className="ncf-rates-acknowledgement"><input type="checkbox" checked={financialReviewed} disabled={busy} onChange={event => setReviewedSignature(event.target.checked ? currentSignature : null)} />I have reviewed the rates, frequencies and placement fees.<span className="ncf-required-mark">Required</span></label>}
        {hasSelection && <label className="ncf-change-note" htmlFor="ncf-change-note">Explanation for HR (optional)
          <textarea id="ncf-change-note" rows="3" maxLength={MAX_CHANGE_NOTE_LENGTH} value={changeNote} disabled={busy} onChange={event => setChangeNote(event.target.value)} placeholder="Why is this change being made?" />
          <small>{changeNote.length}/{MAX_CHANGE_NOTE_LENGTH}</small>
        </label>}
        {preview && <section>
          <p><strong>Reason:</strong> {preview.reason}</p>
          {changeNote.trim() && <p><strong>Explanation:</strong> {changeNote.trim()}</p>}
          <p>{preview.changes.length} field change{preview.changes.length === 1 ? '' : 's'} will be sent to HR.</p>
          {groupChanges(preview.changes).map(group => <div key={group.name} className="ncf-change-preview-group">
            <h4>{group.name}</h4>
            <div className="ncf-change-list">{group.changes.map(change => <div key={change.sourcePath}><strong>{change.label}</strong><span>{change.previousValue} <b>to</b> {change.currentValue}</span></div>)}</div>
          </div>)}
        </section>}
      </>}
      <div className="modal-actions">
        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
        {preview
          ? <button type="button" className="btn btn-primary" onClick={submit} disabled={busy || ((financialChanged || preview.financialReviewRequired) && !financialReviewed)}>{busy ? 'Submitting...' : 'Submit to HR'}</button>
          : <button type="button" className="btn btn-primary" onClick={review} disabled={busy || loading}>{busy ? 'Reviewing...' : 'Review changes'}</button>}
      </div>
    </div>
  </div>;
}

export default NcfChangeModal;
